import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { WIRE_ROWS } from "@/lib/wireInstructions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt$(n) {
  return "$" + (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ── Icons ─────────────────────────────────────────────────────────────────────

const LockIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

const UnlockIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2"/>
    <path d="M7 11V7a5 5 0 0 1 9.9-1"/>
  </svg>
);

const CheckIcon = ({ size = 48 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#2E7D55" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="9 12 11 14 15 10"/>
  </svg>
);

const CardIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2"/>
    <line x1="1" y1="10" x2="23" y2="10"/>
  </svg>
);

const BankIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 22h18M6 18v-7M10 18v-7M14 18v-7M18 18v-7M12 2L2 7h20L12 2z"/>
  </svg>
);

const DownloadIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const SpinnerIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" style={{ animation: "spin 0.75s linear infinite" }}>
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
  </svg>
);

const ImageIcon = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2"/>
    <circle cx="8.5" cy="8.5" r="1.5"/>
    <polyline points="21 15 16 10 5 21"/>
  </svg>
);

// ── Locked preview grid ───────────────────────────────────────────────────────

function LockedGrid({ count }) {
  const n = Math.min(Math.max(count || 3, 3), 9);
  return (
    <div style={s.lockedWrap}>
      <div style={{ ...s.grid, gridTemplateColumns: n === 1 ? "1fr" : n === 2 ? "1fr 1fr" : "1fr 1fr 1fr" }}>
        {Array.from({ length: n }).map((_, i) => (
          <div key={i} style={s.lockedCell}>
            <ImageIcon size={28} />
          </div>
        ))}
      </div>
      {/* Overlay */}
      <div style={s.lockOverlay}>
        <div style={s.lockBadge}>
          <LockIcon size={20} />
        </div>
        <p style={s.lockLabel}>{count > 0 ? `${count} file${count !== 1 ? "s" : ""} locked` : "Files locked"}</p>
        <p style={s.lockSub}>Complete payment below to unlock</p>
      </div>
    </div>
  );
}

// ── Unlocked image grid ───────────────────────────────────────────────────────

function UnlockedGrid({ images }) {
  const cols = images.length === 1 ? "1fr" : images.length === 2 ? "1fr 1fr" : "1fr 1fr 1fr";
  return (
    <div style={{ ...s.grid, gridTemplateColumns: cols }}>
      {images.map((img, i) => (
        <div key={i} style={s.imageCell} onClick={() => window.open(img.url, "_blank")}>
          <img
            src={img.url}
            alt={img.caption || img.filename || `File ${i + 1}`}
            style={s.image}
            onError={e => { e.target.style.display = "none"; }}
          />
          <div style={s.imageHover}>
            <DownloadIcon />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Upload icon ───────────────────────────────────────────────────────────────

const UploadIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="17 8 12 3 7 8"/>
    <line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
);

// ── Wire Transfer Panel ───────────────────────────────────────────────────────

function WirePanel({ amount, clientToken, onBack }) {
  const [uploadState, setUploadState] = useState("idle"); // idle | uploading | done | error
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useState(null);

  const rows = WIRE_ROWS;

  const handleFile = async (file) => {
    if (!file) return;
    setUploadState("uploading");
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("note", "Client-uploaded wire confirmation");
      await axios.post(`${API}/deliveries/client/${clientToken}/wire-confirmation`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadState("done");
      toast.success("Confirmation uploaded! We'll verify and unlock your files shortly.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed — please try again.");
      setUploadState("error");
    }
  };

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <p style={{ fontSize: 13, color: "#302E2B", margin: 0 }}>
        Send <strong style={{ color: "#000000" }}>{fmt$(amount)}</strong> to the account below, then upload your confirmation receipt below.
      </p>

      {/* Wire details */}
      <div style={s.wireCard}>
        {rows.map(([label, value]) => (
          <div key={label} style={s.wireRow}>
            <span style={s.wireLabel}>{label}</span>
            <span style={s.wireValue}>{value}</span>
          </div>
        ))}
      </div>

      <div style={s.infoBox}>
        <span style={{ fontSize: 13, color: "#3D6355", lineHeight: 1.55 }}>
          Questions? Email <strong>accounting@therenderpros.com</strong> with your transaction reference number.
        </span>
      </div>

      {/* Upload confirmation */}
      {uploadState === "done" ? (
        <div style={{ ...s.successBanner, marginTop: 0 }}>
          <CheckIcon size={18} />
          <span>Confirmation received — we'll unlock your files within 1 business day.</span>
        </div>
      ) : (
        <div>
          <p style={{ fontSize: 11, fontWeight: 450, letterSpacing: "1.2px", textTransform: "uppercase", color: "#8A8783", margin: "0 0 8px" }}>
            Upload Wire Confirmation
          </p>
          <label
            style={{
              ...s.uploadZone,
              background: dragOver ? "#ECF0EA" : "#F4F2EE",
              borderColor: dragOver ? "#3D6355" : uploadState === "error" ? "#E28E76" : "#D5D3CE",
              cursor: uploadState === "uploading" ? "not-allowed" : "pointer",
            }}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
          >
            <input
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.webp,.heic"
              style={{ display: "none" }}
              onChange={onFileChange}
              disabled={uploadState === "uploading"}
            />
            {uploadState === "uploading" ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                <SpinnerIcon size={22} />
                <span style={{ fontSize: 13, color: "#8A8783" }}>Uploading…</span>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                <div style={{ color: "#3D6355" }}><UploadIcon /></div>
                <span style={{ fontSize: 13, fontWeight: 450, color: "#000000" }}>Upload Confirmation</span>
                <span style={{ fontSize: 12, color: "#8A8783" }}>PDF, PNG, JPG or screenshot · drag & drop or click</span>
              </div>
            )}
          </label>
        </div>
      )}

      <button style={s.ghostBtn} onClick={onBack}>← Back to payment options</button>
    </div>
  );
}

// ── Payment Selection ─────────────────────────────────────────────────────────

function PaymentSection({ delivery, clientToken }) {
  const [mode, setMode] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleStripe = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/deliveries/client/${clientToken}/stripe-checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start checkout");
      setLoading(false);
    }
  };

  if (mode === "wire") {
    return <WirePanel amount={delivery.amount} clientToken={clientToken} onBack={() => setMode(null)} />;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <button
        style={{ ...s.primaryBtn, opacity: loading ? 0.6 : 1 }}
        disabled={loading}
        onClick={handleStripe}
      >
        <CardIcon />
        {loading ? "Redirecting to Stripe…" : `Pay ${fmt$(delivery.amount)} with Card`}
      </button>
      <button style={s.secondaryBtn} onClick={() => setMode("wire")}>
        <BankIcon />
        Pay via Wire Transfer
      </button>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function DeliveryClient() {
  const { token: clientToken } = useParams();
  const [searchParams] = useSearchParams();
  const [delivery, setDelivery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pollingSession, setPollingSession] = useState(null);

  const fetchDelivery = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/deliveries/client/${clientToken}`);
      setDelivery(res.data);
      return res.data;
    } catch (e) {
      if (e.response?.status !== 404) toast.error("Failed to load delivery");
      return null;
    } finally {
      setLoading(false);
    }
  }, [clientToken]);

  const pollStripeStatus = useCallback(async (sessionId) => {
    try {
      const res = await axios.get(`${API}/deliveries/client/${clientToken}/checkout-status/${sessionId}`);
      if (res.data.is_paid) {
        setDelivery(prev => ({ ...prev, payment_status: "paid", images: res.data.images, dropbox_link: res.data.dropbox_link }));
        toast.success("Payment confirmed! Your files are now unlocked.");
        setPollingSession(null);
      }
      return res.data.is_paid;
    } catch { return false; }
  }, [clientToken]);

  useEffect(() => { fetchDelivery(); }, [fetchDelivery]);

  useEffect(() => {
    const sid = searchParams.get("checkout_session_id");
    const cancelled = searchParams.get("checkout_cancelled");
    if (sid) setPollingSession(sid);
    else if (cancelled) toast.info("Payment cancelled — you can try again below.");
  }, [searchParams]);

  useEffect(() => {
    if (!pollingSession) return;
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      const paid = await pollStripeStatus(pollingSession);
      if (paid || attempts > 20) { clearInterval(interval); setPollingSession(null); }
    }, 2000);
    return () => clearInterval(interval);
  }, [pollingSession, pollStripeStatus]);

  const isPaid = delivery?.payment_status === "paid";
  const isPolling = !!pollingSession;

  // Loading
  if (loading) return (
    <div style={s.page}>
      <div style={s.card}>
        <Logo />
        <div style={s.centered}><SpinnerIcon size={22} /><p style={{ ...s.muted, marginTop: 10 }}>Loading your delivery…</p></div>
      </div>
    </div>
  );

  // Not found
  if (!delivery) return (
    <div style={s.page}>
      <div style={s.card}>
        <Logo />
        <div style={s.centered}>
          <p style={s.sectionTitle}>Delivery not found</p>
          <p style={s.muted}>This link may be invalid or expired.<br />Please contact your project manager.</p>
        </div>
      </div>
    </div>
  );

  return (
    <div style={s.page}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .img-cell:hover .img-hover { opacity: 1 !important; }
        .pay-btn:hover { opacity: 0.88; transform: translateY(-1px); }
        .ghost-btn:hover { background: #F4F2EE; }
      `}</style>

      <div style={s.card}>
        <Logo />

        {/* Doc badge */}
        <div style={s.badgeRow}>
          <span style={s.badge}>Final Delivery</span>
        </div>

        {/* Title */}
        <h1 style={s.title}>{delivery.title || "Your Deliverables"}</h1>
        {delivery.program_name && <div style={s.chip}><span style={s.chipInner}>{delivery.program_name}</span></div>}
        {delivery.notes && (
          <p style={{ ...s.body, marginTop: 16, textAlign: "left", borderLeft: "2px solid #D5D3CE", paddingLeft: 14 }}>
            {delivery.notes}
          </p>
        )}

        <div style={s.divider} />

        {/* ── Files section ── */}
        <div style={{ marginBottom: 24 }}>
          <div style={s.fileHeader}>
            <span style={s.sectionLabel}>
              {isPaid ? <UnlockIcon size={13} /> : <LockIcon size={13} />}
              &nbsp;Delivery Files ({delivery.image_count || delivery.images?.length || 0})
            </span>
          </div>

          {isPaid ? (
            delivery.images?.length > 0
              ? <UnlockedGrid images={delivery.images} />
              : <p style={{ ...s.muted, textAlign: "center", padding: "24px 0" }}>No image previews — use the download link below.</p>
          ) : (
            <LockedGrid count={delivery.image_count} />
          )}
        </div>

        {/* ── Paid: success banner + download ── */}
        {isPaid && (
          <>
            <div style={s.successBanner}>
              <CheckIcon size={20} />
              <span>Payment received — all files are unlocked</span>
            </div>
            {delivery.dropbox_link && (
              <button
                style={{ ...s.primaryBtn, marginTop: 12 }}
                onClick={() => window.open(delivery.dropbox_link, "_blank")}
              >
                <DownloadIcon />
                Download All Files
              </button>
            )}
          </>
        )}

        {/* ── Polling ── */}
        {isPolling && !isPaid && (
          <div style={s.infoBox}>
            <SpinnerIcon size={15} />
            <span style={{ fontSize: 13, color: "#3D6355" }}>Confirming your payment…</span>
          </div>
        )}

        {/* ── Wire pending ── */}
        {!isPaid && delivery.payment_method === "wire" && !isPolling && (
          <div style={s.infoBox}>
            <span style={{ fontSize: 13, color: "#3D6355", lineHeight: 1.55 }}>
              <strong>Wire transfer pending.</strong> Your files will unlock automatically once we confirm receipt.
              Please refresh this page after sending.
            </span>
          </div>
        )}

        {/* ── Payment card ── */}
        {!isPaid && !isPolling && (
          <div style={s.payCard}>
            <div style={s.payHeader}>
              <div>
                <div style={s.priceLabel}>Total Due</div>
                <div style={s.priceAmount}>{fmt$(delivery.amount)}</div>
              </div>
              <div style={s.lockPill}>
                <LockIcon size={13} />
                <span>Locked</span>
              </div>
            </div>
            <p style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", margin: "0 0 20px", lineHeight: 1.5 }}>
              Payment instantly unlocks all {delivery.image_count || ""} files and your full download link.
            </p>
            <PaymentSection delivery={delivery} clientToken={clientToken} />
          </div>
        )}

        {/* Trust row */}
        <div style={s.trustRow}>
          <span style={s.trustItem}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            SSL encrypted
          </span>
          <span style={s.trustDot}/>
          <span style={s.trustItem}>Powered by Stripe</span>
          <span style={s.trustDot}/>
          <span style={s.trustItem}>The Render Pros</span>
        </div>
      </div>

      <p style={{ marginTop: 24, fontSize: 11, color: "#9E9B96", textAlign: "center" }}>
        © {new Date().getFullYear()} The Render Pros · All rights reserved
      </p>
    </div>
  );
}

function Logo() {
  return (
    <div style={{ display: "flex", justifyContent: "center", marginBottom: 28 }}>
      <img src="/trp-logo-new.png" alt="The Render Pros" style={{ height: 34, objectFit: "contain" }} />
    </div>
  );
}

const s = {
  page: {
    position: "fixed",
    inset: 0,
    overflowY: "auto",
    background: "#F4F2EE",
    fontFamily: 'var(--font-sans)',
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "52px 16px 48px",
    zIndex: 9999,
  },
  card: {
    background: "#fff",
    borderRadius: 24,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06), 0 12px 40px rgba(0,0,0,0.08)",
    padding: "44px 48px 40px",
    maxWidth: 600,
    width: "100%",
  },
  badgeRow: { display: "flex", justifyContent: "center", marginBottom: 20 },
  badge: {
    display: "inline-block",
    padding: "5px 14px",
    background: "#F4F2EE",
    borderRadius: 100,
    fontSize: 11,
    fontWeight: 450,
    letterSpacing: "1px",
    color: "#565350",
    textTransform: "uppercase",
  },
  title: {
    fontSize: 28,
    fontWeight: 300,
    letterSpacing: "-0.6px",
    color: "#000000",
    margin: "0 0 8px",
    textAlign: "center",
    lineHeight: 1.2,
  },
  chip: { display: "flex", justifyContent: "center", marginBottom: 4 },
  chipInner: {
    display: "inline-block",
    padding: "4px 14px",
    background: "#F4F2EE",
    borderRadius: 100,
    fontSize: 12,
    color: "#302E2B",
    fontWeight: 450,
  },
  body: { fontSize: 14, lineHeight: 1.7, color: "#302E2B", margin: 0 },
  muted: { fontSize: 13, color: "#8A8783", margin: 0, lineHeight: 1.6 },
  centered: { display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "24px 0 8px" },
  divider: { height: 1, background: "#F4F2EE", margin: "24px 0" },
  sectionLabel: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    fontSize: 10,
    fontWeight: 450,
    letterSpacing: "1.4px",
    color: "#8A8783",
    textTransform: "uppercase",
  },
  fileHeader: { marginBottom: 14 },
  grid: { display: "grid", gap: 8 },
  lockedWrap: { position: "relative", borderRadius: 14, overflow: "hidden" },
  lockedCell: {
    aspectRatio: "1",
    background: "#F4F2EE",
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#D5D3CE",
    filter: "blur(1px)",
  },
  lockOverlay: {
    position: "absolute",
    inset: 0,
    background: "rgba(255,255,255,0.72)",
    backdropFilter: "blur(4px)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
  },
  lockBadge: {
    width: 48,
    height: 48,
    borderRadius: "50%",
    background: "#fff",
    border: "1px solid #D5D3CE",
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#000000",
    marginBottom: 4,
  },
  lockLabel: { fontSize: 14, fontWeight: 450, color: "#000000", margin: 0 },
  lockSub: { fontSize: 12, color: "#8A8783", margin: 0 },
  imageCell: {
    aspectRatio: "1",
    borderRadius: 10,
    overflow: "hidden",
    cursor: "pointer",
    position: "relative",
    background: "#F4F2EE",
  },
  image: { width: "100%", height: "100%", objectFit: "cover", display: "block", transition: "transform 0.2s" },
  imageHover: {
    position: "absolute",
    inset: 0,
    background: "rgba(0,0,0,0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    opacity: 0,
    transition: "opacity 0.15s",
  },
  successBanner: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "14px 18px",
    background: "#E9F3EC",
    border: "1px solid #D2E7DA",
    borderRadius: 12,
    color: "#21593D",
    fontSize: 14,
    fontWeight: 450,
    marginBottom: 4,
  },
  payCard: {
    background: "#000000",
    borderRadius: 18,
    padding: "28px 28px 24px",
    marginTop: 4,
  },
  payHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 6,
  },
  priceLabel: {
    fontSize: 10,
    fontWeight: 450,
    letterSpacing: "1.5px",
    color: "rgba(255,255,255,0.45)",
    textTransform: "uppercase",
    marginBottom: 6,
  },
  priceAmount: {
    fontSize: 42,
    fontWeight: 300,
    letterSpacing: "-1.4px",
    color: "#fff",
    fontVariantNumeric: "tabular-nums",
    lineHeight: 1,
  },
  lockPill: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    padding: "5px 12px",
    background: "rgba(255,255,255,0.1)",
    borderRadius: 100,
    fontSize: 11,
    fontWeight: 450,
    color: "rgba(255,255,255,0.6)",
    marginTop: 6,
  },
  primaryBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    width: "100%",
    padding: "15px 0",
    background: "#3D6355",
    color: "#fff",
    fontSize: 15,
    fontWeight: 450,
    border: "none",
    borderRadius: 12,
    cursor: "pointer",
    transition: "opacity 0.15s, transform 0.1s",
    boxShadow: "0 4px 14px rgba(0,113,227,0.35)",
    letterSpacing: "-0.1px",
  },
  secondaryBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    width: "100%",
    padding: "14px 0",
    background: "rgba(255,255,255,0.08)",
    color: "rgba(255,255,255,0.8)",
    fontSize: 14,
    fontWeight: 450,
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 12,
    cursor: "pointer",
    transition: "opacity 0.15s",
    letterSpacing: "-0.1px",
  },
  ghostBtn: {
    background: "none",
    border: "none",
    fontSize: 13,
    color: "#3D6355",
    cursor: "pointer",
    padding: "4px 0",
    fontFamily: "inherit",
  },
  wireCard: {
    background: "#F4F2EE",
    border: "1px solid #F1EFEA",
    borderRadius: 12,
    padding: "16px 18px",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  wireRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: 13,
  },
  wireLabel: { color: "#8A8783", flexShrink: 0 },
  wireValue: { fontWeight: 450, color: "#000000", fontFamily: "monospace", textAlign: "right" },
  infoBox: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    padding: "12px 16px",
    background: "#ECF0EA",
    border: "1px solid #C3D0C8",
    borderRadius: 10,
    marginTop: 4,
    marginBottom: 4,
  },
  trustRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    marginTop: 24,
    paddingTop: 20,
    borderTop: "1px solid #F4F2EE",
    flexWrap: "wrap",
  },
  trustItem: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    color: "#9E9B96",
    fontWeight: 450,
  },
  trustDot: { width: 3, height: 3, borderRadius: "50%", background: "#D5D3CE", flexShrink: 0 },
  sectionTitle: { fontSize: 16, fontWeight: 300, color: "#000000", margin: 0 },
  uploadZone: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px 16px",
    border: "1.5px dashed #D5D3CE",
    borderRadius: 12,
    transition: "border-color 0.15s, background 0.15s",
    minHeight: 100,
  },
};
